import type { TVariableProps } from "@/dialogs/share-dialog/types";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { openDialog } from "@store/dialogs";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { AddDataDialog } from "../add-data-dialog";
import { CsvFileIcon } from "../csv-file-icon";
import { toMatrix, type DataMatrix } from "../data-grid";
import { readMatrixCsvFile } from "../matrix-csv";
import {
  readStructuredCsvFile,
  type StructuredCsvResult,
} from "../structured-csv";
import { createListRecord } from "../structured-values";
import { ScalarControl } from "./scalar-control";

type StructuredValue = Array<Record<string, unknown>>;

interface ImporterProps {
  expectedHeaders?: string[];
  onImport: (records: StructuredValue) => void;
}

const CsvImporter = ({ expectedHeaders, onImport }: ImporterProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<StructuredCsvResult | null>(null);
  const [error, setError] = useState("");

  const selectFile = async (file: File | undefined) => {
    if (!file) return;
    setError("");
    try {
      const result = await readStructuredCsvFile(file);
      if (
        expectedHeaders &&
        (result.headers.length !== expectedHeaders.length ||
          result.headers.some((header) => !expectedHeaders.includes(header)))
      ) {
        throw new Error(
          `CSV columns must match: ${expectedHeaders.join(", ")}`,
        );
      }
      setPreview(result);
    } catch (exception) {
      setPreview(null);
      setError(
        exception instanceof Error ? exception.message : "Could not read CSV",
      );
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
      <input
        ref={inputRef}
        className="tw-hidden"
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => void selectFile(event.target.files?.[0])}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-shadow-2xs"
      >
        <CsvFileIcon className="tw-size-4" />
        Upload CSV
      </button>
      {preview && (
        <div
          role="status"
          className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-bg-[#f3f2f5] tw-p-2 tw-text-xs tw-text-blue"
        >
          {preview.records.length} rows ready
          <button
            type="button"
            onClick={() => {
              onImport(preview.records);
              setPreview(null);
            }}
            className="tw-rounded-md tw-border-none tw-bg-primary tw-px-2 tw-py-1 tw-font-medium tw-text-white"
          >
            Import
          </button>
          <button
            type="button"
            onClick={() => setPreview(null)}
            className="tw-bg-transparent tw-border-none tw-text-blue-500"
          >
            Cancel
          </button>
        </div>
      )}
      {error && (
        <span role="alert" className="tw-text-xs tw-text-[#D43548]">
          {error}
        </span>
      )}
    </div>
  );
};

const RemoveButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    aria-label="Remove row"
    onClick={onClick}
    className="tw-flex tw-size-7 tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-text-blue-500 hover:tw-bg-[#f3f2f5]"
  >
    <IconTrash className="tw-size-4 !tw-fill-none" />
  </button>
);

interface ListProps {
  childVariables: TVariableProps[];
  value: StructuredValue;
  onChange: (value: StructuredValue) => void;
  invalid?: boolean;
}

/**
 * Row-fill editor for a LIST variable (Figma 90:48023): one card per item, each
 * holding the list's sub-fields as label / value rows, then "+ Add more" and
 * "Upload CSV". A list is cloned once per item at render time, so the sender is
 * really filling in rows here.
 */
export const ListControl = ({
  childVariables,
  value,
  onChange,
  invalid = false,
}: ListProps) => {
  const rows = Array.isArray(value) ? value : [];
  const updateField = (rowIndex: number, name: string, fieldValue: string) => {
    const next = rows.map((row, index) =>
      index === rowIndex ? { ...row, [name]: fieldValue } : row,
    );
    onChange(next);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          className={cn(
            "tw-rounded-lg tw-border tw-border-solid tw-border-black/5 tw-bg-white tw-p-4",
            invalid && "tw-border-[#D43548]",
          )}
        >
          <div className="tw-mb-2 tw-flex tw-items-center tw-justify-between">
            <span className="tw-font-sans tw-text-sm tw-font-medium tw-leading-6 tw-text-blue">
              #{rowIndex + 1}
            </span>
            <RemoveButton
              onClick={() =>
                onChange(rows.filter((_, index) => index !== rowIndex))
              }
            />
          </div>
          <div>
            {childVariables.map((child, childIndex) => (
              <div
                key={child.name}
                className={cn(
                  "tw-grid tw-grid-cols-2 tw-items-center tw-gap-4 tw-px-2 tw-py-3",
                  childIndex !== childVariables.length - 1 &&
                    "tw-border-b tw-border-solid tw-border-[#e7e6ec]",
                )}
              >
                <span className="tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-blue">
                  {child.title || child.name}
                </span>
                <ScalarControl
                  value={String(row[child.name] ?? "")}
                  placeholder="Enter value"
                  onChange={(fieldValue) =>
                    updateField(rowIndex, child.name, fieldValue)
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <button
          type="button"
          onClick={() => onChange([...rows, createListRecord(childVariables)])}
          className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-shadow-2xs"
        >
          <IconPlus className="tw-size-4 !tw-fill-none" />
          Add more
        </button>
        <CsvImporter
          expectedHeaders={childVariables.map((child) => child.name)}
          onImport={onChange}
        />
      </div>
    </div>
  );
};

interface DataProps {
  value: unknown;
  onChange: (value: DataMatrix) => void;
  invalid?: boolean;
  /** Name of the CSV this data came from, when it was uploaded rather than typed. */
  fileName?: string;
  onFileNameChange?: (fileName: string | undefined) => void;
}

/** Ghost "Upload CSV" affordance used in the data row (Figma 111:34811). */
const UploadCsvButton = ({
  onFile,
}: {
  onFile: (file: File) => void | Promise<void>;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        className="tw-hidden"
        type="file"
        accept=".csv,text/csv"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onFile(file);
          event.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="tw-bg-transparent tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap tw-border-none tw-px-1 tw-font-sans tw-text-sm tw-font-normal tw-text-blue"
      >
        <CsvFileIcon className="tw-size-4" />
        Upload CSV
      </button>
    </>
  );
};

const IconAction = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="tw-bg-transparent tw-flex tw-size-7 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-text-blue-500 hover:tw-bg-[#f3f2f5]"
  >
    {children}
  </button>
);

/**
 * Data-variable control: a compact row that opens the "Add data" spreadsheet
 * (Figma 111:34811 empty state, 111:34173 uploaded-CSV state). The value is a
 * 2D matrix — see data-grid.ts — because the whole table is populated from it.
 */
export const DataControl = ({
  value,
  onChange,
  invalid = false,
  fileName,
  onFileNameChange,
}: DataProps) => {
  const dispatch = useAppDispatch();
  const matrix = toMatrix(value);
  const hasData = matrix.length > 0;

  const commit = (next: DataMatrix, nextFileName?: string) => {
    onChange(next);
    onFileNameChange?.(nextFileName);
  };

  const openSpreadsheet = () => {
    dispatch(
      openDialog({
        id: AddDataDialog.id,
        content: AddDataDialog,
        props: { value, onSubmit: commit },
      }),
    );
  };

  const importCsv = async (file: File) => {
    try {
      // Lenient MATRIX parse: a data variable is positional, so empty and
      // duplicate header cells, blank rows and ragged rows are valid content
      // (the strict record parser stays on the legacy LIST import above).
      commit(await readMatrixCsvFile(file), file.name);
    } catch (exception) {
      toast.error(
        exception instanceof Error ? exception.message : "Could not read CSV",
      );
    }
  };

  const clear = () => commit([], undefined);

  return (
    <div
      className={cn(
        "tw-flex tw-min-w-0 tw-items-center tw-justify-end tw-gap-2",
        invalid && "tw-rounded-lg tw-outline tw-outline-1 tw-outline-[#D43548]",
      )}
    >
      {hasData ? (
        <>
          <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-font-sans tw-text-sm tw-font-normal tw-text-blue">
            <CsvFileIcon className="tw-size-4" />
            <span className="tw-min-w-0 tw-truncate" title={fileName}>
              {/* Typed-in data has no file name — summarise the sheet instead. */}
              {fileName ??
                `${matrix.length} row${matrix.length === 1 ? "" : "s"} × ${matrix[0]?.length ?? 0}`}
            </span>
          </span>
          <IconAction label="Edit data" onClick={openSpreadsheet}>
            <IconPencil className="tw-size-4 !tw-fill-none" />
          </IconAction>
          <IconAction label="Remove data" onClick={clear}>
            <IconTrash className="tw-size-4 !tw-fill-none" />
          </IconAction>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={openSpreadsheet}
            className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-gap-2 tw-whitespace-nowrap tw-rounded-lg tw-border tw-border-solid tw-border-blue/10 tw-bg-white tw-px-3 tw-font-sans tw-text-sm tw-font-normal tw-text-blue tw-shadow-2xs"
          >
            <IconPlus className="tw-size-4 !tw-fill-none" />
            Add data
          </button>
          <UploadCsvButton onFile={importCsv} />
        </>
      )}
    </div>
  );
};
